import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Button, IconHighlight, ModalLayout } from '@aimsos/ui';

import { useModalEscape } from '../hooks/useModalEscape.js';
import { ModalFooter } from './parts';

import { Trash2 } from 'lucide-react';

/**
 * Destructive confirm for deleting a widget (CT-5187 / DS-3).
 *
 * `ModalLayout` + `useBodyPortal` — no `createPortal`, no `fixed inset-0`, no
 * hand-rolled focus trap. Shape follows
 * `aims-os-governance/src/domains/sandbox-plane/components/DeleteBundleDialog`.
 *
 * `useTranslation` is called **inside** the modal rather than receiving
 * pre-translated strings, and a `submittingRef` guard makes a double-click on
 * the primary button delete once.
 */
function DeleteWidgetModal({ isOpen, widget, onClose, onConfirm }) {
  const { t } = useTranslation('dashboards');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const submittingRef = useRef(false);
  const isMountedRef = useRef(false);

  // Set (not just cleared) inside the effect body itself: under StrictMode's
  // dev-only setup->cleanup->setup double-invoke, a cleanup-only assignment
  // would flip this to `false` after the very first mount and never flip it
  // back, permanently disabling the confirm button for the component's whole
  // lifetime.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleClose = useCallback(() => {
    if (submittingRef.current) return;
    setSubmitError('');
    onClose?.();
  }, [onClose]);

  const handleConfirm = useCallback(async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError('');
    try {
      await onConfirm?.(widget);
      if (isMountedRef.current) onClose?.();
    } catch (error) {
      // The row stays in the list: nothing is removed until the service says so.
      if (isMountedRef.current) setSubmitError(error?.message || t('widgetLibrary.delete.error'));
    } finally {
      submittingRef.current = false;
      if (isMountedRef.current) setSubmitting(false);
    }
  }, [onClose, onConfirm, t, widget]);

  useModalEscape(isOpen && Boolean(widget), handleClose, { disabled: submitting });
  if (!isOpen || !widget) return null;

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={handleClose}
      useBodyPortal
      maxWidthClassName="max-w-md"
      titlePrefix={<IconHighlight icon={Trash2} bgColor="Error" iconColor="Error" size="medium" />}
      title={t('widgetLibrary.delete.title')}
      subtitle={widget.name}
      closeDisabled={submitting}
    >
      <div className="px-6 py-4">
        <p className="text-sm text-content-secondary">
          {t('widgetLibrary.delete.body', { name: widget.name })}
        </p>
      </div>

      <ModalFooter
        cancelLabel={t('widgetLibrary.delete.cancel')}
        onCancel={handleClose}
        submitting={submitting}
        submitError={submitError}
        primaryAction={
          <Button variant="danger" onClick={handleConfirm} className="!text-[12px]">
            {t('widgetLibrary.delete.confirm')}
          </Button>
        }
      />
    </ModalLayout>
  );
}

export default DeleteWidgetModal;
